import type { SessionTypes } from '@walletconnect/types';
import { W3M_API_URL, WalletConnectChainID } from './constants';

export function getDefaultChainFromSession(
	session: SessionTypes.Struct,
	selectedChain: WalletConnectChainID,
): WalletConnectChainID {
	const chains = session.namespaces['solana']?.accounts.map((account) => `solana:${account.split(':')[1]}`);

	if (selectedChain === WalletConnectChainID.Mainnet) {
		if (chains?.find((chain) => chain === WalletConnectChainID.Mainnet)) {
			return WalletConnectChainID.Mainnet;
		}
		console.warn('The connected wallet is using a deprecated chain ID for Solana.');
		return WalletConnectChainID.Deprecated_Mainnet;
	} else if (selectedChain === WalletConnectChainID.Devnet) {
		if (chains?.find((chain) => chain === WalletConnectChainID.Devnet)) {
			return WalletConnectChainID.Devnet;
		}
		console.warn('The connected wallet is using a deprecated chain ID for Solana.');
		return WalletConnectChainID.Deprecated_Devnet;
	}
	throw Error('WalletConnect Solana Adapter: Unable to get a default chain from the session.');
}

type RequestArgs = { path: string; params: Record<string, string | undefined>; signal?: AbortSignal };
function createUrl({ path, params }: RequestArgs) {
	const url = new URL(path, W3M_API_URL);
	Object.entries(params).forEach(([key, value]) => {
		if (value) {
			url.searchParams.append(key, value);
		}
	});
	return url;
}

async function fetchData(...args: Parameters<typeof fetch>) {
	const response = await fetch(...args);
	if (!response.ok) {
		const err = new Error(`HTTP status code: ${response.status}`, {
			cause: response,
		});
		throw err;
	}
	return response;
}

export async function w3mget<T>({ path, params, signal }: RequestArgs) {
	const url = createUrl({ path, params });
	const response = await fetchData(url, { method: 'GET', signal });
	return response.json() as T;
}

export async function w3fetchBlob({ path, params, signal }: RequestArgs) {
	const url = createUrl({ path, params });
	const response = await fetchData(url, { method: 'GET', signal });
	return response.blob();
}

export function formatNativeUrl(appUrl: string, wcUri: string, universalLink: string | null = null) {
	if (appUrl.startsWith('http://') || appUrl.startsWith('https://')) {
		let safeAppUrl = appUrl;
		if (!safeAppUrl.endsWith('/')) {
			safeAppUrl = `${safeAppUrl}/`;
		}
		const encodedWcUrl = encodeURIComponent(wcUri);
		return {
			redirect: `${safeAppUrl}wc?uri=${encodedWcUrl}`,
			href: safeAppUrl,
		};
	}

	let safeAppUrl = appUrl;
	let safeUniversalLink = universalLink;

	if (!safeAppUrl.includes('://')) {
		safeAppUrl = appUrl.replaceAll('/', '').replaceAll(':', '');
		safeAppUrl = `${safeAppUrl}://`;
	}

	if (!safeAppUrl.endsWith('/')) {
		safeAppUrl = `${safeAppUrl}/`;
	}

	if (safeUniversalLink && !safeUniversalLink?.endsWith('/')) {
		safeUniversalLink = `${safeUniversalLink}/`;
	}

	const encodedWcUrl = encodeURIComponent(wcUri);

	return {
		redirect: `${safeAppUrl}wc?uri=${encodedWcUrl}`,
		redirectUniversalLink: safeUniversalLink ? `${safeUniversalLink}wc?uri=${encodedWcUrl}` : undefined,
		href: safeAppUrl,
	};
}
